import { useState } from "react";
import { useTranslation } from "react-i18next";
import SessionCard from "./SessionCard";
import type { Session } from "@/types";

interface SessionListProps {
  sessions: Session[];
  isLoading?: boolean;
  /** Vehicle id to filter by (controlled mode when provided). */
  vehicleFilter?: string;
  onVehicleFilterChange?: (vehicleId: string) => void;
  /** Show ownership clues on cards (All Sessions scope only). */
  showOwner?: boolean;
  /** Signed-in user's id, forwarded to cards for the "You" chip. */
  currentUserId?: string;
}

export default function SessionList({
  sessions,
  isLoading,
  vehicleFilter,
  onVehicleFilterChange,
  showOwner,
  currentUserId,
}: SessionListProps) {
  const { t } = useTranslation("pages");
  const [sortBy, setSortBy] = useState<"date" | "track" | "vehicle">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [filterTrack, setFilterTrack] = useState("");
  const [internalVehicleFilter, setInternalVehicleFilter] = useState("");

  const isVehicleFilterControlled = vehicleFilter !== undefined;
  const filterVehicleId = isVehicleFilterControlled
    ? vehicleFilter
    : internalVehicleFilter;

  const handleVehicleFilterChange = (vehicleId: string) => {
    if (!isVehicleFilterControlled) {
      setInternalVehicleFilter(vehicleId);
    }
    onVehicleFilterChange?.(vehicleId);
  };

  if (isLoading) {
    return (
 <div className="flex flex-col gap-2">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
 className="bg-white rounded-lg border border-secondary-200 px-4 py-3 animate-pulse"
          >
 <div className="h-4 bg-secondary-200 rounded w-1/3 mb-2"></div>
 <div className="h-3 bg-secondary-200 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
 <div className="text-center py-12">
 <p className="text-lg text-secondary-500 mb-2">
          {t("sessions.noSessionsMessage")}
        </p>
 <p className="text-sm text-secondary-400">
          {t("sessions.createFirstSession")}
        </p>
      </div>
    );
  }

  // Get unique tracks and vehicles for filtering (vehicles keyed by id)
  const uniqueTracks = [
    ...new Set(sessions.map((s) => s.event.track.name)),
  ].sort();
  const vehicleOptions = Array.from(
    new Map(
      sessions.map((s) => [
        s.vehicle.id,
        s.vehicle.name ||
          `${s.vehicle.year} ${s.vehicle.maker} ${s.vehicle.model}`,
      ]),
    ).entries(),
  ).sort((a, b) => a[1].localeCompare(b[1]));

  // Filter sessions
  let filteredSessions = sessions.filter((session) => {
    const trackMatch =
      !filterTrack || session.event.track.name === filterTrack;
    const vehicleMatch =
      !filterVehicleId || session.vehicle.id === filterVehicleId;
    return trackMatch && vehicleMatch;
  });

  // Sort sessions
  filteredSessions.sort((a, b) => {
    let aValue: string | Date;
    let bValue: string | Date;

    switch (sortBy) {
      case "date":
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
        break;
      case "track":
        aValue = a.event.track.name;
        bValue = b.event.track.name;
        break;
      case "vehicle":
        aValue =
          a.vehicle.name ||
          `${a.vehicle.year} ${a.vehicle.maker} ${a.vehicle.model}`;
        bValue =
          b.vehicle.name ||
          `${b.vehicle.year} ${b.vehicle.maker} ${b.vehicle.model}`;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const handleSortChange = (newSortBy: "date" | "track" | "vehicle") => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(newSortBy);
      setSortOrder("desc");
    }
  };

  return (
 <div className="space-y-3 sm:space-y-6">
      {/* Filters and Sorting */}
 <div className="bg-white rounded-lg border border-secondary-200 p-3 sm:p-4">
 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
 <label className="block text-sm font-medium text-secondary-700 mb-2">
              {t("sessions.sortBy")}
            </label>
 <div className="flex space-x-2">
              {(["date", "track", "vehicle"] as const).map((option) => {
                const translationKey =
                  option === "date"
                    ? "sessions.sortByDate"
                    : option === "track"
                      ? "sessions.sortByTrack"
                      : "sessions.sortByVehicle";
                return (
                  <button
                    key={option}
                    onClick={() => handleSortChange(option)}
 className={`px-3 py-1 text-sm rounded-lg ${
                      sortBy === option
                        ? "bg-primary-600 text-white"
                        : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                    }`}
                  >
                    {t(translationKey)}
                    {sortBy === option && (
 <span className="ml-1">
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
 <label className="block text-sm font-medium text-secondary-700 mb-2">
              {t("sessions.filterByTrack")}
            </label>
            <select
              value={filterTrack}
              onChange={(e) => setFilterTrack(e.target.value)}
 className="input"
            >
              <option value="">{t("sessions.allTracks")}</option>
              {uniqueTracks.map((track) => (
                <option key={track} value={track}>
                  {track}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="vehicle-filter"
              className="block text-sm font-medium text-secondary-700 mb-2"
            >
              {t("sessions.filterByVehicle")}
            </label>
            <select
              id="vehicle-filter"
              value={filterVehicleId}
              onChange={(e) => handleVehicleFilterChange(e.target.value)}
              className="input"
            >
              <option value="">{t("sessions.allVehicles")}</option>
              {vehicleOptions.map(([vehicleId, label]) => (
                <option key={vehicleId} value={vehicleId}>
                  {label}
                </option>
              ))}
            </select>
          </div>

 <div className="flex items-end">
 <div className="text-sm text-secondary-600">
              {t("sessions.sessionCount", {
                count: filteredSessions.length,
                total: sessions.length,
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Session rows */}
 <div className="flex flex-col gap-2">
        {filteredSessions.map((session) => (
          <SessionCard
            key={session.id}
            session={session}
            showOwner={showOwner}
            currentUserId={currentUserId}
          />
        ))}
      </div>
    </div>
  );
}
